import { Outlet, createRoute, redirect } from '@tanstack/react-router';
import { rootRoute } from './root.js';
import { isAuthenticated } from '../auth/AuthContext.js';
import { getAccessToken } from '../auth/tokenStore.js';
import { decodeJwtRoleClaim } from '../auth/jwtClaims.js';
import { AuthedLayout } from '../components/AuthedLayout.js';

/**
 * Authenticated layout route — mounted as a "layout route" (no path of
 * its own). Every authenticated ADMIN page nests under this. The
 * beforeLoad gate handles two cases:
 *   1. Unauthenticated → bounce to /login.
 *   2. Authenticated as `employee` role → bounce to /me. Employees
 *      have their own portal and shouldn't see admin pages even if
 *      they manually type a URL.
 *
 * The /v1/* APIs would 403 anyway, but routing prevents the brief
 * flash of admin-looking layout while the API call fails.
 */
export const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed', // no `path` → layout route, mounted at root
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { next: location.pathname },
      });
    }
    const token = getAccessToken();
    const role = token ? decodeJwtRoleClaim(token) : null;
    if (role === 'employee') {
      throw redirect({ to: '/me' });
    }
  },
  component: AuthedLayoutComponent,
});

function AuthedLayoutComponent(): JSX.Element {
  return (
    <AuthedLayout>
      <Outlet />
    </AuthedLayout>
  );
}
